import React, { useEffect, useState, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    Box,
    Avatar,
    Button,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Stack,
    Chip,
    AvatarGroup,
    Hidden, // For demonstrating conditional rendering if needed
    Grid,
    createTheme,
    ThemeProvider,
    CssBaseline,
    IconButton,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,

} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatList from '../Layout/ChatList';
import FriendsList from '../Layout/FriendList';
import FollowersList from '../Layout/FollowersList';
import SocialFeed from '../SocialFeed';
import GroupJoinedList from '../GroupJoined';
import PostModal from '../Layout/PostModal';
import { get, post, put } from '../../utility/fetch';
import { get as gets, post as posts } from '../../utility/fetchWeb';

import PhotoCamera from '@mui/icons-material/PhotoCamera';
import GroupJoinModal from '../Layout/GroupJoinModal';
import notification from '../../utility/notification';
// import GroupCreated from '../GroupCreated';
import { format } from 'date-fns';
import { Cancel, CancelRounded, ExitToApp, TimeToLeaveRounded } from '@mui/icons-material';
import Media from '../Layout/Media';
import FriendRequests from '../FriendRequests';
import { useLocation, useNavigate } from 'react-router-dom';
import UploadButton from '../Layout/UploadButton';

// Example profile image and cover photo
const profilePic = 'https://i.imgur.com/example-profile-pic.jpg';
const initialProfilePic = 'https://placehold.co/150x150/0000FF/FFFFFF?text=GP';
const initialCoverPhoto = 'https://placehold.co/1200x300/CCCCCC/FFFFFF?text=Cover+Photo';
// Custom MUI theme for consistent styling
const theme = createTheme({
    palette: {
        primary: {
            main: '#28a745',
        },
        secondary: {
            main: '#f8f9fa',
        },
        text: {
            primary: '#333',
            secondary: '#6c757d',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h4: {
            fontWeight: 600,
            fontSize: '2rem',
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        body1: {
            fontSize: '1rem',
            '@media (max-width:600px)': {
                fontSize: '0.9rem',
            },
        },
        caption: {
            fontSize: '0.8rem',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '25px',
                    paddingLeft: '18px',
                    paddingRight: '18px',
                },
                containedPrimary: {
                    backgroundColor: '#28a745',
                    '&:hover': {
                        backgroundColor: '#218838',
                    }
                },
                outlined: {
                    borderColor: '#ced4da',
                    color: '#6c757d',
                    '&:hover': {
                        borderColor: '#adb5bd',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                }
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: '4px solid white',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#6c757d',
                    '&.Mui-selected': {
                        color: '#28a745',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#28a745',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: '#e0e0e0',
                    color: '#333',
                    borderRadius: '8px',
                    fontWeight: 500,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: '25px',
                    backgroundColor: '#f5f5f5',
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#e0e0e0',
                },
                root: {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#adb5bd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28a745',
                    },
                },
            },
        },
    },
});

function ProfilePage() {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => {
        // Initialize tab from location state if available
        return location.state?.activeTab ?? 0;
    });
    const [openPostModal, setOpenPostModal] = useState(false);
    const [posts, setPosts] = useState([])
    const [selectedGroup, setSelectedGroup] = useState(
        () => location?.state?.selectedGroup || null
    );
    const [openJoinGroupModal, setOpenJoinGroupModal] = useState(false);

    // New state for profile and cover photo URLs
    const [profilePicUrl, setProfilePicUrl] = useState(initialProfilePic);
    const [coverPhotoUrl, setCoverPhotoUrl] = useState(initialCoverPhoto);

    // State for remove member confirmation dialog
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    // State for leave group confirmation dialog
    const [leaveModalOpen, setLeaveModalOpen] = useState(false);

    // Add this state for image upload
    const [docUploading, setDocUploading] = useState(false);
    const navigate = useNavigate()

    // Combine the useEffects for better state management
    useEffect(() => {
        // Handle both group selection and tab changes from navigation
        if (location.state) {
            if (location.state.selectedGroup) {
                console.log(location.state.selectedGroup)
                setSelectedGroup(location.state.selectedGroup);
                setActiveTab(0); // Switch to Social Feed tab
            } else if (location.state.activeTab !== undefined) {
                setActiveTab(location.state.activeTab);
            }
        }

        // Clear location state after processing to prevent unwanted tab switches on refresh
        if (location.state) {
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Update URL without navigation
        window.history.replaceState(
            { activeTab: newValue },
            document.title
        );
    };

    const handleOpenPostModal = () => {
        setOpenPostModal(true)
    }

    const handleClosePostModal = () => {
        setOpenPostModal(false);
    }

    const submitPost = async (postData) => {
        console.log('New Post Data:', postData);
        let res = await post(`/posts`, postData); // Using mock post
        if (res) {
            notification({ // Using mock notification
                type: 'success',
                message: 'Post created successfully!',
                duration: 3000,
            })
            getPosts(selectedGroup?.id); // Using mock getPosts
            setOpenPostModal(false);
        }
    }

    const getPosts = async (id) => {
        try {
            const res = await gets(
                `/posts/get-group-posts-including-comments?groupId=${id}&pageNumber=1&pageSize=10`
            );

            if (res?.posts?.length > 0) {
                setPosts(res?.posts);
            } else {
                setPosts([]); // Reset if no posts found
            }

            console.log("Fetched posts:", res);
        } catch (error) {
            console.error("Error fetching posts:", error);
            setPosts([]); // Clear posts on error
        }
    };


    const isGroupAdmin = selectedGroup &&
        selectedGroup.communityGroupAdmin?.employee?.id === Number(sessionStorage.getItem("userId"));

    console.log(selectedGroup)
    const coverPhotoToShow = selectedGroup?.pictureUrl || initialCoverPhoto;

    console.log(coverPhotoToShow)


    useEffect(() => {
        if (selectedGroup?.id) {
            getPosts(selectedGroup?.id);
            handleTabChange(null, 6); // Switch to Social Feed tab when a group is selected
        }
    }, [selectedGroup]);

    // If group data was passed through navigation
    useEffect(() => {
        if (location.state?.selectedGroup) {
            setSelectedGroup(location.state.selectedGroup);
            setActiveTab(0); // Switch to Social Feed tab
        } else if (location.state?.defaultTab !== undefined) {
            setActiveTab(location.state.defaultTab);
        }
    }, [location.state]);

    // Function to handle member removal
    const handleRemoveMember = (member) => {
        setSelectedMember(member);
        setRemoveModalOpen(true);
    };

    const handleConfirmRemove = async () => {
        try {
            const response = await put(
                `/communitygroup/${selectedGroup.id}/community-group-member/${selectedMember.id}/remove-community-group-member`
            );

            if (response.statusCode === 200) {
                // Update the group members list locally
                const updatedMembers = selectedGroup.communityGroupMembers.filter(
                    m => m.id !== selectedMember.id
                );

                setSelectedGroup({
                    ...selectedGroup,
                    communityGroupMembers: updatedMembers
                });

                notification({
                    type: 'success',
                    message: 'Member removed successfully',
                    duration: 3000
                });
            }
        } catch (error) {
            console.error('Error removing member:', error);
            notification({
                type: 'error',
                message: 'Failed to remove member',
                duration: 3000
            });
        } finally {
            setRemoveModalOpen(false);
            setSelectedMember(null);
        }
    };

    // Function to handle leaving the group
    const handleLeaveGroup = async () => {
        try {
            const currentMember = Number(sessionStorage.getItem("userId"));


            if (!currentMember) {
                throw new Error('Member not found');
            }

            const response = await put(
                `/communitygroup/${selectedGroup.id}/community-group-member/${currentMember}/leave-community-group`
            );

            if (response.statusCode === 200) {
                notification({
                    type: 'success',
                    message: 'Successfully left the group',
                    duration: 3000
                });
                setSelectedGroup(null);
                setActiveTab(0); // Switch back to Group Joined tab
            }
        } catch (error) {
            console.error('Error leaving group:', error);
            notification({
                type: 'error',
                message: 'Failed to leave group',
                duration: 3000
            });
        } finally {
            setLeaveModalOpen(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ backgroundColor: theme.palette.secondary.main, width: "100vw", minHeight: '100vh' }}>
                {/* Cover Photo Area */}
                {selectedGroup &&
                    <Box
                        sx={{
                            width: '100%',
                            height: { xs: 180, sm: 250, md: 300 }, // Responsive height
                            backgroundImage: `url("${coverPhotoToShow}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'relative', // For absolute positioning of profile pic and button
                        }}
                    >
                        {/* Only show Change Cover Photo button if user is group admin and a group is selected, or if no group is selected */}
                        {(!selectedGroup || isGroupAdmin) && (
                            <UploadButton
                                acceptedFiles="image/*"
                                buttonText="Change Cover Photo"
                                onUploadSuccess={(response) => {
                                    if (response?.code === 1) {
                                        // Update only the pictureUrl
                                        if (selectedGroup) {
                                            const updatedGroup = {
                                                ...selectedGroup,
                                                pictureUrl: response.doclink, // New photo only
                                                categoryId: selectedGroup.category?.id || selectedGroup.categoryId, // Ensure categoryId is included
                                            };

                                            // Send update to backend
                                            put(`/communitygroup/${selectedGroup.id}`, updatedGroup).then(res => {
                                                if (res?.statusCode === 200) {
                                                    setSelectedGroup(prev => ({
                                                        ...prev,
                                                        pictureUrl: response.doclink,
                                                    }));
                                                    notification({
                                                        type: 'success',
                                                        message: 'Cover photo updated successfully',
                                                    });
                                                } else {
                                                    notification({
                                                        type: 'error',
                                                        message: res?.error?.message || 'Failed to update group photo',
                                                    });
                                                }
                                            }).catch(() => {
                                                notification({
                                                    type: 'error',
                                                    message: 'Failed to update group photo',
                                                });
                                            });
                                        } else {
                                            // If not in a group, treat it as profile picture update
                                            setCoverPhotoUrl(response.doclink);
                                            notification({
                                                type: 'success',
                                                message: 'Cover photo updated successfully',
                                            });
                                        }
                                    }
                                }}
                                onUploadError={() => {
                                    notification({ type: 'error', message: 'Cover photo upload failed' });
                                }}
                                buttonProps={{
                                    sx: {
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        },
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                    },
                                }}
                            />

                        )}
                    </Box>

                }

                {/* Main Content Area */}
                <Container maxWidth="xl" sx={{ mt: { xs: 8, sm: 10 }, py: 3 }}>

                    <Grid container spacing={3}>
                        {/* Profile Info and Action Buttons */}
                        <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' }, textAlign: { xs: 'center', md: 'left' } }}>
                            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: { xs: 0, md: 2 } }}>
                                {selectedGroup?.name || 'Socials & Community'}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                {/* Public. 560 Friends. 68 posts */}
                            </Typography>

                            <Box sx={{ width: "100%", mb: 3, display: 'flex', flexDirection: { sm: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                                {/* Friend Avatars */}
                                <AvatarGroup spacing={"small"} max={6} sx={{ mb: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                    <Avatar alt="Remy Sharp" src="https://via.placeholder.com/40" />
                                    <Avatar alt="Travis Howard" src="https://via.placeholder.com/40" />
                                    <Avatar alt="Cindy Baker" src="https://via.placeholder.com/40" />
                                    <Avatar alt="Agnes Walker" src="https://via.placeholder.com/40" />
                                    <Avatar alt="Trevor Henderson" src="https://via.placeholder.com/40" />
                                    <Avatar alt="Another Friend" src="https://via.placeholder.com/40" />
                                    <Avatar alt="Yet Another Friend" src="https://via.placeholder.com/40" />
                                </AvatarGroup>

                                {/* Action Buttons */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    sx={{
                                        width: '100%',
                                        justifyContent: { xs: 'center', md: 'flex-end' },
                                        flexWrap: 'wrap',
                                        '& > *': { mb: { xs: 1.5, sm: 0 } },
                                        mt: { xs: 2, md: 0 },
                                    }}
                                >
                                    {Number(selectedGroup?.id && isGroupAdmin) > 0 && <Button onClick={handleOpenPostModal} variant="contained" startIcon={<AddIcon />}>
                                        Create a Post
                                    </Button>}
                                    {/* <Button variant="outlined" startIcon={<ShareIcon />}>
                                        Share
                                    </Button> */}
                                    <Button onClick={() => { setActiveTab(2) }} variant="contained" startIcon={<PersonAddIcon />}>
                                        Find Friends
                                    </Button>
                                    {/* <Button onClick={() => setOpenJoinGroupModal(true)} variant="contained" startIcon={<GroupAddIcon />}>
                                        Join a Group
                                    </Button> */}
                                    {/* Leave Group button - only show if user is not admin */}
                                    {selectedGroup && !isGroupAdmin && (
                                        <Button
                                            onClick={() => setLeaveModalOpen(true)}
                                            variant="contained"
                                            color="error"
                                            startIcon={<ExitToApp />}
                                        >
                                            Leave Group
                                        </Button>
                                    )}
                                </Stack>
                            </Box>
                        </Grid>

                        {/* Find a Group Search Field */}
                        <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, alignItems: 'flex-start', order: { xs: -1, md: 0 } }}>
                            {/* <TextField
                                variant="outlined"
                                placeholder="Find a Group"
                                size="small"
                                sx={{ width: { xs: '100%', sm: 300 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                        </InputAdornment>
                                    ),
                                }}
                            /> */}
                        </Grid>
                    </Grid>

                    {/* Navigation Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: { xs: 4, md: 6 } }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="profile navigation tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                '& .MuiTabs-flexContainer': {
                                    justifyContent: { xs: 'flex-start', sm: 'center', md: 'flex-start' },
                                },
                            }}
                        >
                            {/* <Tab disabled={!selectedGroup} label="Group Info" /> */}

                            <Tab label="General Chat" />
                            <Tab label="My Friends" />
                            <Tab label="Friend Requests" /> {/* Add new tab */}
                            {/* <Tab label="Media" /> */}
                            {/* <Tab label="Created Groups" /> */}
                            {/* <Tab label="Group Joined" />
                            <Tab disabled={!selectedGroup} label="Conversations" /> */}
                        </Tabs>
                    </Box>

                    {/* Tab Content - (Render content based on activeTab) */}
                    <Box sx={{ mt: 4 }}>


                        {
                            activeTab === 0 && (
                                <ChatList />
                            )
                        }

                        {
                            activeTab === 1 && (
                                <FriendsList />
                            )
                        }

                        {
                            activeTab === 2 && (
                                <FriendRequests />
                            )
                        }

                        {/* {
                            activeTab === 3 && (
                                <Media groupId={selectedGroup?.id} />
                            )
                        } */}

                        {/* {
                            activeTab === 5 && (
                                <GroupCreated selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} />
                            )
                        } */}


                        {/* Add content for other tabs here */}
                    </Box>
                </Container>

                <PostModal id={selectedGroup?.id} open={openPostModal} onSubmit={submitPost} handleClose={handleClosePostModal} />

                <GroupJoinModal open={openJoinGroupModal} onClose={() => setOpenJoinGroupModal(false)} />

                {/* Confirmation dialog for removing group member */}
                <Dialog
                    open={removeModalOpen}
                    onClose={() => setRemoveModalOpen(false)}
                >
                    <DialogTitle>Remove Member</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to remove {selectedMember?.patient?.firstName || selectedMember?.employee?.firstName} from this group?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setRemoveModalOpen(false)}
                            color="primary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmRemove}
                            color="error"
                            variant="contained"
                        >
                            Remove
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirmation dialog for leaving group */}
                <Dialog
                    open={leaveModalOpen}
                    onClose={() => setLeaveModalOpen(false)}
                >
                    <DialogTitle>Leave Group</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to leave this group?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setLeaveModalOpen(false)}
                            color="primary"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleLeaveGroup}
                            color="error"
                            variant="contained"
                        >
                            Leave Group
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}

export default ProfilePage;
